import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Badge, Button, Card, Col, Container, Form, InputGroup,
  Modal, Row, Spinner, Table
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { toISODate } from '../utils/thaiDate'

const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const currentStudyYear = () => new Date().getFullYear() + 543

const padBook = (b) => String(b).padStart(2, '0')
const padSeq  = (s) => String(s).padStart(4, '0')

export default function BankAccounts() {
  const navigate = useNavigate()

  const [accounts,    setAccounts]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [search,      setSearch]      = useState('')
  const [yearFilter,  setYearFilter]  = useState('')

  // open-account modal
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({
    study_year:      String(currentStudyYear()),
    book_no:         '1',
    gender:          '1',
    st_query:        '',
    st_id:           '',
    st_name:         '',
    open_date:       new Date(),
    initial_balance: '',
  })
  const [nextSeq,        setNextSeq]        = useState(null)
  const [studentHints,   setStudentHints]   = useState([])
  const [saving,         setSaving]         = useState(false)
  const [modalError,     setModalError]     = useState('')
  const [accountNo,      setAccountNo]      = useState('')
  const [accountEdited,  setAccountEdited]  = useState(false)
  const [bookNoEdited,   setBookNoEdited]   = useState(false)
  const [isDuplicate,    setIsDuplicate]    = useState(false)
  const [checkingDup,    setCheckingDup]    = useState(false)

  // close-account confirm
  const [closingId,    setClosingId]    = useState(null)
  const [closeLoading, setCloseLoading] = useState(false)

  const fetchAccounts = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (search)     params.set('search',     search)
      if (yearFilter) params.set('study_year', yearFilter)
      const res = await axiosInstance.get(`/api/bank/accounts?${params}`)
      if (res.data.success) setAccounts(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลบัญชีไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [search, yearFilter])

  useEffect(() => { fetchAccounts() }, [fetchAccounts])

  // preview next account number
  useEffect(() => {
    if (!showModal || !form.study_year || !form.book_no) { setNextSeq(null); return }
    axiosInstance
      .get(`/api/bank/accounts?study_year=${form.study_year}`)
      .then(res => {
        if (res.data.success) {
          const filtered = res.data.data.filter(
            a => String(a.study_year) === String(form.study_year) &&
                 String(a.book_no)    === String(form.book_no)
          )
          const maxSeq = filtered.reduce((m, a) => Math.max(m, a.account_seq), 0)
          setNextSeq(maxSeq + 1)
        }
      })
      .catch(() => setNextSeq(1))
  }, [showModal, form.study_year, form.book_no])

  // Auto-fill book_no: latest book used for this year+gender (from account_no format YYYY-Gender-BookNo-Seq)
  useEffect(() => {
    if (!showModal || !form.study_year || !form.gender || bookNoEdited) return
    axiosInstance
      .get(`/api/bank/accounts?study_year=${form.study_year}`)
      .then(res => {
        if (res.data.success) {
          const gStr = String(form.gender)
          const maxBook = res.data.data.reduce((m, a) => {
            if (String(a.study_year) !== String(form.study_year)) return m
            const parts = (a.account_no || '').split('-')
            if (parts.length >= 4 && parts[1] === gStr) return Math.max(m, Number(a.book_no) || 0)
            return m
          }, 0)
          setForm(f => ({ ...f, book_no: String(maxBook > 0 ? maxBook : 1) }))
        }
      })
      .catch(() => {})
  }, [showModal, form.study_year, form.gender, bookNoEdited])

  // Auto-generate account number (YYYY-Gender-BookNo-Seq)
  useEffect(() => {
    if (!accountEdited && nextSeq && form.study_year && form.book_no && form.gender) {
      setAccountNo(`${form.study_year}-${form.gender}-${padBook(form.book_no)}-${padSeq(nextSeq)}`)
    }
  }, [nextSeq, form.study_year, form.book_no, form.gender, accountEdited])

  // Reset account-number states when modal closes
  useEffect(() => {
    if (!showModal) {
      setAccountNo('')
      setAccountEdited(false)
      setBookNoEdited(false)
      setIsDuplicate(false)
    }
  }, [showModal])

  // Debounced duplicate check against database
  useEffect(() => {
    if (!accountNo) { setIsDuplicate(false); return }
    setCheckingDup(true)
    const timer = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(`/api/bank/accounts?search=${encodeURIComponent(accountNo)}`)
        if (res.data.success) {
          setIsDuplicate(res.data.data.some(a => a.account_no === accountNo))
        }
      } catch {
        setIsDuplicate(false)
      } finally {
        setCheckingDup(false)
      }
    }, 500)
    return () => { clearTimeout(timer); setCheckingDup(false) }
  }, [accountNo])

  const searchStudents = async (q) => {
    if (q.length < 2) { setStudentHints([]); return }
    try {
      const res = await axiosInstance.get(`/api/bank/students/search?q=${encodeURIComponent(q)}&gender=${form.gender}`)
      setStudentHints(res.data?.data || [])
    } catch (_) {
      setStudentHints([])
    }
  }

  const regenerateAccountNo = () => {
    setAccountEdited(false)
    if (nextSeq && form.study_year && form.book_no && form.gender) {
      setAccountNo(`${form.study_year}-${form.gender}-${padBook(form.book_no)}-${padSeq(nextSeq)}`)
    }
  }

  const handleStudentSelect = (s) => {
    const g = s.gender ?? s.sex ?? ''
    const gCode = (g === 'f' || g === 'F' || g === '2' || g === 'หญิง' || g === 'female') ? '2' : '1'
    setForm(f => ({ ...f, st_id: s.st_id, st_name: s.st_name, st_query: s.st_name, gender: gCode }))
    setStudentHints([])
  }

  const handleOpenAccount = async (e) => {
    e.preventDefault()
    if (!form.st_id) { setModalError('กรุณาเลือกนักเรียน'); return }
    if (!accountNo)  { setModalError('กรุณาระบุเลขบัญชี'); return }
    if (isDuplicate) { setModalError('เลขบัญชีนี้มีอยู่ในระบบแล้ว'); return }
    setSaving(true)
    setModalError('')
    try {
      const body = {
        study_year:      Number(form.study_year),
        book_no:         Number(form.book_no),
        gender:          Number(form.gender),
        account_no:      accountNo,
        st_id:           form.st_id,
        open_date:       toISODate(form.open_date),
        initial_balance: form.initial_balance !== '' ? parseFloat(form.initial_balance) : null,
      }
      const res = await axiosInstance.post('/api/bank/accounts', body)
      if (res.data.success) {
        setShowModal(false)
        fetchAccounts()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'เปิดบัญชีไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = async (id) => {
    setCloseLoading(true)
    try {
      await axiosInstance.patch(`/api/bank/accounts/${id}/close`)
      setClosingId(null)
      fetchAccounts()
    } catch (err) {
      setError(err.response?.data?.message || 'ปิดบัญชีไม่สำเร็จ')
      setClosingId(null)
    } finally {
      setCloseLoading(false)
    }
  }

  const closingAccount = accounts.find(a => a.account_id === closingId)

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          {/* Title */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-bank-card-line me-2 text-primary" />
              บัญชีนักเรียน
            </h4>
            <Button variant="primary" size="sm" onClick={() => { setShowModal(true); setModalError(''); setAccountEdited(false) }}>
              <i className="ri-add-line me-1" />เปิดบัญชีใหม่
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {/* Filter bar */}
          <Card className="card-one mb-3">
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col xs={12} md={6}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="ri-search-line" /></InputGroup.Text>
                    <Form.Control
                      placeholder="ค้นหาเลขบัญชี, ชื่อ, หรือรหัสนักเรียน"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Control
                    size="sm"
                    placeholder="ปีการศึกษา (เช่น 2567)"
                    value={yearFilter}
                    onChange={e => setYearFilter(e.target.value)}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Button size="sm" variant="outline-secondary" onClick={() => { setSearch(''); setYearFilter('') }}>
                    <i className="ri-refresh-line me-1" />ล้างตัวกรอง
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Table */}
          <Card className="card-one">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>เลขบัญชี</th>
                      <th>ชื่อนักเรียน</th>
                      <th>รหัสนักเรียน</th>
                      <th>ปีการศึกษา</th>
                      <th className="text-end">ยอดคงเหลือ</th>
                      <th>วันที่เปิด</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-muted py-4">ไม่พบข้อมูลบัญชี</td></tr>
                    ) : accounts.map(a => (
                      <tr
                        key={a.account_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/bank/account/${a.account_id}`)}
                      >
                        <td><code>{a.account_no}</code></td>
                        <td>{a.st_name}</td>
                        <td>{a.st_idnumber}</td>
                        <td>{a.study_year}</td>
                        <td className="text-end">฿{fmt(a.current_balance)}</td>
                        <td>{a.open_date?.slice(0, 10)}</td>
                        <td>
                          <Badge bg={a.account_status === 'active' ? 'success' : 'secondary'}>
                            {a.account_status === 'active' ? 'เปิดอยู่' : 'ปิดแล้ว'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Open account modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">เปิดบัญชีใหม่</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleOpenAccount}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}

            <Row className="g-2 mb-2">
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">ปีการศึกษา</Form.Label>
                <Form.Control
                  size="sm"
                  value={form.study_year}
                  onChange={e => { setForm(f => ({ ...f, study_year: e.target.value })); setBookNoEdited(false) }}
                  required
                />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เพศ</Form.Label>
                <Form.Select
                  size="sm"
                  value={form.gender}
                  onChange={e => { setForm(f => ({ ...f, gender: e.target.value })); setBookNoEdited(false) }}
                >
                  <option value="1">ชาย (1)</option>
                  <option value="2">หญิง (2)</option>
                </Form.Select>
              </Col>
              <Col xs={3}>
                <Form.Label className="fs-sm mb-1">เล่มที่</Form.Label>
                <Form.Control
                  size="sm"
                  type="number" min="1"
                  value={form.book_no}
                  onChange={e => { setForm(f => ({ ...f, book_no: e.target.value })); setBookNoEdited(true) }}
                  required
                />
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="fs-sm mb-0">
                  เลขบัญชี
                  {checkingDup && <Spinner animation="border" size="sm" className="ms-2" style={{ width: '0.75rem', height: '0.75rem' }} />}
                </Form.Label>
                {accountEdited && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 fs-sm text-decoration-none"
                    onClick={regenerateAccountNo}
                  >
                    <i className="ri-refresh-line me-1" />สร้างเลขอัตโนมัติ
                  </Button>
                )}
              </div>
              <Form.Control
                size="sm"
                value={accountNo}
                onChange={e => { setAccountNo(e.target.value); setAccountEdited(true) }}
                isInvalid={isDuplicate}
                isValid={accountNo && !isDuplicate && !checkingDup}
                placeholder="กำลังสร้างเลขบัญชี..."
                required
              />
              {isDuplicate && (
                <Form.Control.Feedback type="invalid">
                  เลขบัญชีนี้มีอยู่ในระบบแล้ว
                </Form.Control.Feedback>
              )}
              <Form.Text className="text-muted">รูปแบบ: ปี-เพศ(1/2)-เล่ม-ลำดับ | แก้ไขได้หากต้องการ</Form.Text>
            </Form.Group>

            <Form.Group className="mb-2 position-relative">
              <Form.Label className="fs-sm mb-1">ค้นหานักเรียน</Form.Label>
              <Form.Control
                size="sm"
                placeholder="พิมพ์ชื่อหรือรหัสนักเรียน..."
                value={form.st_query}
                onChange={e => {
                  setForm(f => ({ ...f, st_query: e.target.value, st_id: '', st_name: '' }))
                  searchStudents(e.target.value)
                }}
                autoComplete="off"
              />
              {studentHints.length > 0 && (
                <div className="border rounded bg-white shadow-sm position-absolute w-100" style={{ zIndex: 1050, top: '100%' }}>
                  {studentHints.map(s => (
                    <div
                      key={s.st_id}
                      className="px-3 py-2 fs-sm cursor-pointer hover-bg-light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleStudentSelect(s)}
                    >
                      {s.st_name} <span className="text-muted">({s.st_idnumber})</span>
                    </div>
                  ))}
                </div>
              )}
              {form.st_id && (
                <Form.Text className="text-success">
                  <i className="ri-check-line me-1" />เลือก: {form.st_name}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">วันที่เปิดบัญชี</Form.Label>
              <div>
                <DatePicker
                  selected={form.open_date}
                  onChange={d => setForm(f => ({ ...f, open_date: d }))}
                  dateFormat="dd/MM/yyyy"
                  className="form-control form-control-sm"
                />
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label className="fs-sm mb-1">ยอดเงินตั้งต้น (บาท) <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text>฿</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="ไม่ระบุ"
                  value={form.initial_balance}
                  onChange={e => setForm(f => ({ ...f, initial_balance: e.target.value }))}
                />
              </InputGroup>
              <Form.Text className="text-muted">หากระบุ ระบบจะบันทึกเป็นรายการฝากแรกอัตโนมัติ</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving || isDuplicate || checkingDup}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
              เปิดบัญชี
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Close-account confirm modal */}
      <Modal show={!!closingId} onHide={() => setClosingId(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ยืนยันปิดบัญชี</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>คุณต้องการปิดบัญชีนี้ใช่หรือไม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้</div>
          {closingAccount && (
            <div className="mt-3 border-top pt-3">
              <div className="text-muted fs-sm">ยอดคงเหลือปัจจุบัน</div>
              <div className="fw-bold">฿{fmt(closingAccount.current_balance)}</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setClosingId(null)}>ยกเลิก</Button>
          <Button variant="danger" size="sm" disabled={closeLoading} onClick={() => handleClose(closingId)}>
            {closeLoading ? <Spinner size="sm" className="me-1" /> : null}
            ยืนยันปิดบัญชี
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
